import { Injectable }                   from '@angular/core';
import { ARCHIVING_TSS_LOG_METADATA }   from '@app/modules/archiving/config/archiving-tss-log.config';
import {
	ArchivingTssLog,
	ArchivingTssLogCounterRange,
	ArchivingTssLogCounters
}                                       from '@app/modules/archiving/models/archiving-tss-log.model';
import { ArchivingTss }                 from '@app/modules/archiving/models/archiving-tss.model';
import {
	ArchivingRoutesEnum,
	ArchivingTssLogDetailPage
}                                       from '@app/modules/archiving/routes/archiving.routes';
import {
	ICreateArchivingTssLogExport,
	IPropsUpdateArchivingTssLogState,
	IShowArchivingTssLogExportModalWindowData
}                                       from '@app/modules/archiving/store/archiving-tss-log/archiving-tss-log.actions';
import { archivingTssLogStore }         from '@app/modules/archiving/store/archiving-tss-log/archiving-tss-log.store';
import { BusinessPremisesRoutesEnum }   from '@app/modules/business-premises/routes/business-premises.routes';
import { AbstractBaseService }          from '@core/bases/services/abstractBaseService';
import {
	IArchivingTssHttpParams,
	IArchivingTssLogHttpParams,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                       from '@core/interfaces/api.interface';
import { RouteBasePath }                from '@core/interfaces/route.interface';
import {
	IStoreConfig,
	StoreActionsEnum
}                                       from '@core/interfaces/store.interface';
import { ArchivingTssLogApiService }    from '@http/archiving/archiving-tss-log-api.service';
import { Store }                        from '@ngrx/store';
import { FilterIdentifiersEnum }        from '@shared/list-operations/model/filter.model';
import { ListOperationsIdentifierEnum } from '@shared/list-operations/model/list-operations.model';
import { DateUtils }                    from '@utils/helpers';
import { Observable }                   from 'rxjs';
import {
	map,
	take
}                                       from 'rxjs/operators';


@Injectable( {
	providedIn: 'root'
} )
export class ArchivingTssLogService extends AbstractBaseService {

	constructor(
		private readonly archivingTssLogApiService: ArchivingTssLogApiService,
		protected readonly store$: Store
	) {
		super( store$, archivingTssLogStore.selectors, archivingTssLogStore.actions );
		this.logger.trace( 'ArchivingTssLogService constructor' );
	}

	// Page EVENTS #################################################################################


	// Entity Events ##############################################################################
	private showArchivingTssLogExport( businessPremisesId: string, showOnlyMessage: boolean = false ): void {
		const params: IShowArchivingTssLogExportModalWindowData = {
			caller: 'ArchivingTssLogService:showArchivingTssLogExportModalWindow',
			businessPremisesId,
			showOnlyMessage
		};

		this.store$.dispatch( archivingTssLogStore.actions.showArchivingTssLogExportModalWindow( params ) );
	}

	private createArchivingTssLogExport( archivingTssLogId: string, businessPremisesId: string, archivingTssLogEntityId: string ): void {
		const params: ICreateArchivingTssLogExport = {
			caller: 'ArchivingTssLogService:showArchivingTssLogExportModalWindow',
			archivingTssLogId, businessPremisesId, archivingTssLogEntityId
		};

		this.store$.dispatch( archivingTssLogStore.actions.createArchivingTssLogExport( params ) );
	}

	private generateTssLogBusinessDateTitle( archivingTssLog: ArchivingTssLog ): string {
		const dateFrom = archivingTssLog.businessDateFrom;
		const dateTo = archivingTssLog.businessDateTo;
		const dateFromString = this.dateUtilsHelperService.getLocaleDateString( dateFrom, ARCHIVING_TSS_LOG_METADATA.details.dateFormat );

		if ( dateFrom.getTime() === dateTo.getTime() ) {
			return dateFromString;
		}

		const dateToString = this.dateUtilsHelperService.getLocaleDateString( dateTo, ARCHIVING_TSS_LOG_METADATA.details.dateFormat );
		return dateFromString + ' - ' + dateToString;
	}

	private computeTssLogCountersRange( archivingTssLog: ArchivingTssLog ): ArchivingTssLogCounters {
		let counters: ArchivingTssLogCounters;
		if ( archivingTssLog?.counters ) {
			counters = {
				...archivingTssLog.counters,
				signaturesSum: this.sumTssLogCounterRange( archivingTssLog.counters?.signature ),
				transactionsSum: this.sumTssLogCounterRange( archivingTssLog.counters?.transaction )
			};

			return counters;
		}


		return {
			signaturesSum: archivingTssLog?.signatureCounterFrom && archivingTssLog?.signatureCounterTo
										 ? this.sumTssLogCounterRange( [ { from: archivingTssLog.signatureCounterFrom, to: archivingTssLog.signatureCounterTo } ] )
										 : undefined,

			transactionsSum: archivingTssLog?.transactionCounterFrom && archivingTssLog?.transactionCounterFrom
											 ? this.sumTssLogCounterRange( [ { from: archivingTssLog.transactionCounterFrom, to: archivingTssLog.transactionCounterTo } ] )
											 : undefined
		};
	}

	private sumTssLogCounterRange( archivingTssLogCounterRanges: ArchivingTssLogCounterRange[] ): number {
		if ( !archivingTssLogCounterRanges ) {
			return undefined;
		}

		let sum = 0;
		archivingTssLogCounterRanges.forEach( ( archivingTssLogCounterRange: ArchivingTssLogCounterRange ) => {
			sum += archivingTssLogCounterRange?.to - archivingTssLogCounterRange?.from + 1;
		} );
		return sum;
	}

	// ARCHIVING TSS LOG SPECIFIC SELECTORS ###########################################################################

	// ARCHIVING TSS LOG SPECIFIC ACTIONS ###########################################################################
	updateStoreState( updateArchivingTssLogState: IPropsUpdateArchivingTssLogState ): void {
		this.store$.dispatch( archivingTssLogStore.actions.updateArchivingTssLogState( updateArchivingTssLogState ) );
	}

	showArchivingTssLogExportOnTab( businessPremisesId: string ): void {
		this.showArchivingTssLogExport( businessPremisesId, false );
	}

	createArchivingTssLogExportOnEntity( archivingTssLogId: string, businessPremisesId: string, archivingTssLogEntityId: string ): void {
		this.createArchivingTssLogExport( archivingTssLogId, businessPremisesId, archivingTssLogEntityId );
	}

	createArchivingTssLogExportOnDetailPage( archivingTssLogId: string, businessPremisesId: string, archivingTssLogEntityId: string ): void {
		this.createArchivingTssLogExport( archivingTssLogId, businessPremisesId, archivingTssLogEntityId );
	}


	// NAVIGATION #################################################################################
	navigateToDetailPage( archivingTssLogId: string ): void {
		if ( archivingTssLogId ) {
			const routeDefinition: ArchivingTssLogDetailPage = {
				basePath: RouteBasePath.ArchivingTssLogs,
				path: ArchivingRoutesEnum.ArchivingTssLogDetailPage,
				params: { archivingTssLogId }
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ArchivingTssLogService:navigateToDetailPage'
			);
		}
	}

	navigateBack( caller = '', shouldRestoreListOperationData = true ) {
		this.listOperationsService.state.listOperationsSetRestoreFlag(
			ListOperationsIdentifierEnum.BUSINESS_PREMISES_DETAIL_PAGE_ARCHIVE_TSS_LOGS,
			shouldRestoreListOperationData,
			caller
		);

		this.routerService.navigateBack(
			this.routerService.getFallbackRoute( RouteBasePath.BusinessPremises, BusinessPremisesRoutesEnum.ListPage ),
			caller + ' | ArchivingTssLogService:navigateBack'
		);
	}


	// API #################################################################################
	searchWithStoredListOperationsData(
		businessPremisesId: string,
		listOperationsId: ListOperationsIdentifierEnum,
		caller: string
	): Observable<RequestCompleteListState<ArchivingTssLog>> {
		return this.listOperationsService.helper.requestWithStoredListOperationsData<ArchivingTssLog, IArchivingTssLogHttpParams>(
			( [ businessPremisesIdArg, listOperationsArg, callerArg, storeConfigArg ] ) =>
				this.search( businessPremisesIdArg, listOperationsArg, callerArg, storeConfigArg ),
			[ businessPremisesId, `${ caller } | ArchivingTssLogService:searchWithStoredListOperationsData`, null ],
			1,
			listOperationsId
		);
	}

	search(
		businessPremisesId: string,
		params?: IArchivingTssLogHttpParams,
		caller?: string,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteListState<ArchivingTssLog>> {
		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig = this.createListStoreProcessingConfig<ArchivingTssLog>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<ArchivingTssLog>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingTssLogService:search` };

		let businessDateFrom;
		let businessDateTo;
		if ( params?.activeFilters[ FilterIdentifiersEnum.BUSINESS_DATE ] ) {
			if ( params.activeFilters[ FilterIdentifiersEnum.BUSINESS_DATE ]?.param[ 0 ] ) {
				businessDateFrom = DateUtils.formatDateToLocale(
					params.activeFilters[ FilterIdentifiersEnum.BUSINESS_DATE ]?.param[ 0 ],
					DateUtils.ISO_DATE_FORMAT,
					'en'
				);
			}
			if ( params.activeFilters[ FilterIdentifiersEnum.BUSINESS_DATE ]?.param[ 1 ] ) {
				businessDateTo = DateUtils.formatDateToLocale(
					params.activeFilters[ FilterIdentifiersEnum.BUSINESS_DATE ]?.param[ 1 ],
					DateUtils.ISO_DATE_FORMAT,
					'en'
				);
			}
		}

		params = {
			...this.generateParams( params ) as IArchivingTssLogHttpParams,
			businessPremisesId,
			businessDateFrom,
			businessDateTo
		};

		const req$ = this.archivingTssLogApiService.search( params );
		return this.requestListThroughProxy<ArchivingTssLog>( req$, storeProcessingConfig )
		.pipe(
			take( 1 ),
			map( ( archivingTssLogState: RequestCompleteListState<ArchivingTssLog> ) => {
				if ( archivingTssLogState.requestState === RequestState.SUCCESS ) {
					let updatedEntities: ArchivingTssLog[] = [ ...archivingTssLogState.list ];
					updatedEntities = updatedEntities.map( ( entity: ArchivingTssLog ) => {
						const businessDateTitle = this.generateTssLogBusinessDateTitle( entity );
						const counters = this.computeTssLogCountersRange( entity );
						const updatedEntity = { ...entity, counters, ui: { businessDateTitle } };
						return updatedEntity;
					} );
					this.storeUpsertMany( updatedEntities, `${ caller } | ArchivingTssLogService:search->response` );

					archivingTssLogState = {
						...archivingTssLogState,
						list: updatedEntities
					};
				}
				return archivingTssLogState;
			} )
		);
	}

	read( id: string, withFullResponse?: boolean, caller?: string, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<ArchivingTssLog>> {
		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		let storeProcessingConfig = this.createEntityStoreProcessingConfig<ArchivingTssLog>(
			storeConfig,
			this.storeEntityById$<ArchivingTssLog>( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingTssLogService:read` };

		const req$ = this.archivingTssLogApiService.read( id, withFullResponse );
		return this.requestEntityThroughProxy<ArchivingTssLog>( id, req$, storeProcessingConfig )
		.pipe(
			take( 1 ),
			map( ( archivingTssLogState: RequestCompleteEntityState<ArchivingTssLog> ) => {
				if ( archivingTssLogState.requestState === RequestState.SUCCESS ) {
					const businessDateTitle = this.generateTssLogBusinessDateTitle( archivingTssLogState.entity );
					const counters = this.computeTssLogCountersRange( archivingTssLogState.entity );
					const updatedEntity = { ...archivingTssLogState.entity, counters, ui: { businessDateTitle } };
					this.storeUpsertOne( updatedEntity );

					archivingTssLogState = {
						...archivingTssLogState,
						entity: updatedEntity
					};
				}
				return archivingTssLogState;
			} )
		);
	}

	searchArchivingTssByBusinessPremisesId( businessPremisesId: string, params?: IArchivingTssHttpParams ): Observable<RequestCompleteListState<ArchivingTss>> {
		params = {
			...this.generateParams( params ) as IArchivingTssHttpParams,
			businessPremisesId
		};

		const req$ = this.archivingTssLogApiService.searchTss( params );
		return this.requestListThroughProxy<ArchivingTss>( req$ );
	}
}
